import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import Commons from '../../../../Services/Common/Common';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import { mapParamsBySchema } from '../../../../util';
import { mapRowsBySchema } from '../../../../util';
import { WOR_BASIC_02_Schema } from './WOR_BASIC_02_Schema';

/**
 * 화학물질 노출기준 목록 조회 (GetWorBasic02R2) - 팝업 검색용
 * @param {string} searchWord - 검색어 (유해물질 국문명/영문명/CAS No)
 * @returns {object} useQuery 결과, data = GetWorBasic02R2[]
 */
export const useSearch1 = (searchWord, options = {}) => {
  const { enabled = true } = options;
  const encrypted = Commons.EncryptAES(searchWord ?? '') ?? '';
  const query = useQuery({
    queryKey: ['WorkEnvironment', 'getWorBasic02R2', encrypted],
    queryFn: () =>
      apiRequest('post', Global.urls.WorkEnvironment.getWorBasic02R2, {
        SearchWord: encrypted,
      }),
    enabled: Boolean(enabled),
  });

  const list = (() => {
    if (!query.data) return [];
    const raw = query.data;
    const arr = Array.isArray(raw?.Data) ? raw.Data : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
    return arr.map((row, idx) => ({
      no: idx + 1,
      chemSeq: row.ChemSeq ?? row.chemSeq,
      korNm: row.KorNm ?? row.korNm ?? '',
      engNm: row.EngNm ?? row.engNm ?? '',
      casNo: row.CasNo ?? row.casNo ?? '',
      twaVal: row.TwaVal ?? row.twaVal ?? '',
      stelVal: row.StelVal ?? row.stelVal ?? '',
      remark: row.Remark ?? row.remark ?? '',
    }));
  })();

  return { ...query, chemList: list };
};

/**
 * 대상물질-공정 매트릭스 조회 (GetWorBasic02R1)
 * - 응답: { Data: [ { Columns: [{ Field, HeaderName }], Rows: [...] } ] }
 * - 공정 컬럼은 TB_BS_GONGJONG 기준 동적 피벗
 * - onSuccess(mappedRows, gongjongColumns)
 */
export const useSearch2 = (options = {}) => {
  const { onSuccess } = options;
  const searchInfo = useSelector((state) => state.SearchInfo);
  const context = { search: { ...searchInfo } };
  const params = mapParamsBySchema(WOR_BASIC_02_Schema.mapper.search1, context);

  const query = useQuery({
    queryKey: ['WorkEnvironment', 'getWorBasic02R1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.WorkEnvironment.getWorBasic02R1, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess || !query.data) return;
    const payload = query.data;
    const dataList = payload?.Data ?? payload?.data;
    const first = Array.isArray(dataList) ? dataList[0] : null;
    const columns = first?.Columns ?? first?.columns ?? [];
    const rows = first?.Rows ?? first?.rows ?? [];
    const mappedRows = rows.map((row, idx) => {
      const exposureLimitVal =
        row.ExposureLimit ?? row.exposureLimit ?? '';
      const mapped = {
        no: idx + 1,
        chemSeq: row.MatSeq ?? row.matSeq,
        matSeq: row.MatSeq ?? row.matSeq,
        korNm: row.KorNm ?? row.korNm ?? '',
        twaVal: exposureLimitVal,
        exposureLimit: exposureLimitVal,
        stelVal: '',
        remark: row.Remark ?? row.remark ?? '',
      };
      (columns || []).forEach((col) => {
        const field = col.Field ?? col.field;
        if (field) mapped[field] = row[field] ?? '';
      });
      return mapped;
    });
    onSuccess?.(mappedRows, columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 onSuccess 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * 대상물질-공정 인쇄용 조회 (GetWorBasic02R3)
 * - 서버 쿼리: MatSeq, KorNm, GongjongNm, CheckYn (TB_BS_TARGET_MATERIAL + CROSS JOIN 공정 + LEFT JOIN MAP)
 * - enabled: false → 인쇄 버튼 클릭 시 refetch()로 호출
 * @returns {object} useQuery 결과, data = [{ MatSeq, KorNm, GongjongNm, CheckYn }, ...]
 */
export const useSearch3 = (options = {}) => {
  const searchInfo = useSelector((state) => state.SearchInfo);
  const context = { search: { ...searchInfo } };
  const params = mapParamsBySchema(WOR_BASIC_02_Schema.mapper.search1, context);

  const query = useQuery({
    queryKey: ['WorkEnvironment', 'getWorBasic02R3', params],
    queryFn: () =>
      apiRequest('post', Global.urls.WorkEnvironment.getWorBasic02R3, params),
    enabled: false,
  });

  const printList = (() => {
    if (!query.data) return [];
    const raw = query.data;
    const arr = Array.isArray(raw?.Data)
      ? raw.Data
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : [];
    return arr.map((row) => ({
      matSeq: row.MatSeq ?? row.matSeq,
      korNm: row.KorNm ?? row.korNm ?? '',
      gongjongNm: row.GongjongNm ?? row.gongjongNm ?? '',
      checkYn: row.CheckYn ?? row.checkYn ?? '',
    }));
  })();

  return { ...query, printList };
};

/**
 * 대상물질 삭제 (DeleteWorBasic02D1)
 * - rows 각 항목에 custCd 포함 후 호출 (F에서 처리)
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, WOR_BASIC_02_Schema.mapper.delete1);
    return apiRequest(
      'post1',
      Global.urls.WorkEnvironment.deleteWorBasic02D1,
      payload
    );
  }, options);
};

/**
 * 대상물질-공정 매핑 저장 (SaveWorBasic02U1)
 * 대용량 시 네트워크 오류 방지를 위해 post1 사용 (body만 전송)
 * @param {Array} payload - SaveWorBasic02U1Info[] (CustCd, UserId, MatSeq, ChemSeq?, ExposureLimit?, Remark, CheckedGongjongs)
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (payload) => {
    return apiRequest(
      'post1',
      Global.urls.WorkEnvironment.saveWorBasic02U1,
      payload
    );
  }, options);
};
